// Pure logic for walking through arrangement moves one at a time.
//
// The server returns an ordered move list plus a per-tray plan. To show
// someone standing in the farm what to do at step N, the map has to show
// the farm as it will be *at that step* - after steps 1..N-1 - not as it
// is now or as it will end up. These helpers replay the moves to get
// that state, using the same rules as POST /bench-layout/move:
//   - a full tray moved onto another full tray swaps them
//   - a half tray never swaps; its destination half must be free
//   - a tray from the germination chamber lands in an empty slot
//   - an emptied split cell collapses back to a plain empty slot

export function cellId(loc) {
  return `${loc.bench}/${loc.layer}/${loc.tray}`;
}

// Identifies one physical position: a whole cell, or one half of it.
export function positionId(loc) {
  return loc.half === undefined || loc.half === null
    ? cellId(loc)
    : `${cellId(loc)}/h${loc.half}`;
}

function isHalf(loc) {
  return loc.half !== undefined && loc.half !== null;
}

// state = { cells: { [cellId]: { full, halves } }, chamber: [trayKey] }
// where full is a trayKey or null and halves is [key|null, key|null] or null.
function cloneState(state) {
  const cells = {};
  for (const [id, c] of Object.entries(state.cells)) {
    cells[id] = { full: c.full, halves: c.halves ? [...c.halves] : null };
  }
  return { cells, chamber: [...state.chamber] };
}

export function occupantAt(state, loc) {
  const cell = state.cells[cellId(loc)];
  if (!cell) return null;
  if (isHalf(loc)) return cell.halves ? cell.halves[loc.half] : null;
  return cell.full;
}

function cellFor(state, loc) {
  const id = cellId(loc);
  if (!state.cells[id]) state.cells[id] = { full: null, halves: null };
  return state.cells[id];
}

function place(state, loc, key) {
  const cell = cellFor(state, loc);
  if (isHalf(loc)) {
    if (!cell.halves) cell.halves = [null, null];
    cell.halves[loc.half] = key;
    cell.full = null;
  } else {
    cell.full = key;
    cell.halves = null;
  }
}

function clear(state, loc) {
  const cell = cellFor(state, loc);
  if (isHalf(loc)) {
    if (cell.halves) cell.halves[loc.half] = null;
    if (cell.halves && !cell.halves[0] && !cell.halves[1]) cell.halves = null;
  } else {
    cell.full = null;
  }
}

export function initialState(plan) {
  const state = { cells: {}, chamber: [] };
  for (const entry of plan) {
    if (entry.inChamber) {
      state.chamber.push(entry.trayKey);
    } else if (entry.current) {
      place(state, entry.current, entry.trayKey);
    }
  }
  return state;
}

// Applies one move, returning the new state and whether the move clashed
// with the state it was applied to (which means the list is out of date).
export function applyMove(state, move) {
  const next = cloneState(state);
  const occupant = occupantAt(next, move.to);
  let conflict = false;

  if (move.fromChamber || move.from?.bench === 'germinationChamber') {
    if (!next.chamber.includes(move.trayKey)) conflict = true;
    if (occupant) conflict = true;
    next.chamber = next.chamber.filter((k) => k !== move.trayKey);
    place(next, move.to, move.trayKey);
    return { state: next, conflict };
  }

  if (occupantAt(next, move.from) !== move.trayKey) conflict = true;

  const halfMove = isHalf(move.from) || isHalf(move.to);
  if (occupant && halfMove) conflict = true;

  clear(next, move.from);
  place(next, move.to, move.trayKey);
  if (occupant && !halfMove) place(next, move.from, occupant);

  return { state: next, conflict };
}

// states[i] is the farm immediately before move i; states[moves.length]
// is the farm once every move is done. conflicts[i] flags move i.
export function buildStepStates(plan, moves) {
  const states = [initialState(plan)];
  const conflicts = [];
  for (const move of moves) {
    const { state, conflict } = applyMove(states[states.length - 1], move);
    states.push(state);
    conflicts.push(conflict);
  }
  return { states, conflicts };
}

// The benches a step touches, so a phone can show just those.
export function benchesForMove(move) {
  const names = [];
  for (const loc of [move.from, move.to]) {
    if (loc && loc.bench !== 'germinationChamber' && !names.includes(loc.bench)) {
      names.push(loc.bench);
    }
  }
  return names;
}

// Layer 1 is the bottom shelf. "Top" means the top shelf that is actually
// built, so on a bench with layers 3-4 not installed yet, layer 2 is top.
export function shelfName(layerIndex, bench) {
  const installed = bench?.installedLayers ?? bench?.layers?.length ?? 0;
  if (layerIndex === 0) return 'bottom shelf';
  if (installed > 1 && layerIndex === installed - 1) return 'top shelf';
  return 'middle shelf';
}

export function describeLocation(loc, benches, chamberLocation) {
  if (!loc) return '';
  if (loc.bench === 'germinationChamber') {
    return chamberLocation
      ? `Germination chamber · position ${chamberLocation}`
      : 'Germination chamber';
  }
  const bench = (benches || []).find((b) => b.name === loc.bench);
  const benchLabel = bench?.label || String(loc.bench).replace(/^bench/, 'Bench ');
  const parts = [
    benchLabel,
    `${shelfName(loc.layer, bench)} (L${loc.layer + 1})`,
    `slot ${loc.tray + 1}`,
  ];
  if (isHalf(loc)) parts.push(`half ${loc.half === 0 ? 'A' : 'B'}`);
  return parts.join(' · ');
}

// Plain-language instruction for one step.
export function describeStep(move, { benches, entryByKey, chamberLocationByTrayId } = {}) {
  const chamberLocation = chamberLocationByTrayId?.[move.trayId] || null;
  const swapEntry = move.swap && move.swapWithTrayKey ? entryByKey?.[move.swapWithTrayKey] : null;

  return {
    cropType: move.cropType,
    half: move.size === 'half',
    pickUp: describeLocation(move.from, benches, chamberLocation),
    putDown: describeLocation(move.to, benches),
    swap: move.swap
      ? {
          cropType: move.swapWith || swapEntry?.cropType || 'the tray already there',
          goesTo: describeLocation(move.from, benches),
        }
      : null,
  };
}
